import { useCallback, useState } from 'react';
import { faPlus, faXmark } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';

import {
  ADD_ELEMENT_BLOCKS,
  type AddElementBlockType,
  createAddElementChildBlockInput,
  createAddElementRootBlockInput,
} from '@/components/blocks/add-element-block-catalog';
import { useBlockSelectionStore } from '@/components/blocks/editor/block-selection';
import { ButtonSettingsDialog } from '@/components/blocks/shared/button-settings-dialog';
import { EmbedSettingsDialog } from '@/components/blocks/shared/embed-settings-dialog';
import { ImageSelectorModal } from '@/components/editor/image-selector-modal';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Heading5 } from '@/components/ui/typography';
import { type FormOption, useForms } from '@/hooks/use-forms';
import { normalizeEmbedUrl } from '@/lib/embed-utils';
import { getSectionUiKey } from '@/lib/page-utils';
import { cn, getDocument } from '@/lib/utils';
import { usePageEditorStore } from '@/stores/page-editor-store';
import type { ButtonConfig } from '@/types/button';
import type { SelectedAssetData } from '@/types/image';
import type { PageSection } from '@/types/page-section';

type DeferredModalType = 'image' | 'video' | 'embed' | 'button' | null;

const NEW_BUTTON_INITIAL_VALUE: ButtonConfig = {
  text: 'Button',
  style: 'primary',
  linkType: 'external',
};

function withDefaultForm(
  props: Record<string, unknown>,
  forms: FormOption[],
): Record<string, unknown> {
  const defaultForm = forms.find((f) => f.isDefault) ?? forms[0];
  if (!defaultForm) return props;

  return {
    ...props,
    formId: defaultForm.id,
    form: {
      id: defaultForm.id,
      title: defaultForm.title,
      description: defaultForm.description,
      fields: defaultForm.fields,
      settings: defaultForm.settings,
      thirdParty: defaultForm.thirdParty,
    },
  };
}

interface AddElementButtonProps {
  section: PageSection;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  parentBlockId?: string | null;
  allowedBlockTypes?: readonly AddElementBlockType[];
}

export function AddElementButton({
  section,
  isOpen,
  onOpenChange,
  parentBlockId = null,
  allowedBlockTypes,
}: AddElementButtonProps) {
  const addBlock = usePageEditorStore((state) => state.addBlock);
  const setActiveBlock = useBlockSelectionStore((state) => state.setActiveBlock);
  const { forms } = useForms();
  const [deferredModal, setDeferredModal] = useState<DeferredModalType>(null);
  const availableBlocks = allowedBlockTypes
    ? ADD_ELEMENT_BLOCKS.filter((definition) => allowedBlockTypes.includes(definition.blockType))
    : ADD_ELEMENT_BLOCKS;

  const insertAndScroll = useCallback(
    (blockType: AddElementBlockType, propsOverride?: Record<string, unknown>) => {
      const blockInput = parentBlockId
        ? createAddElementChildBlockInput(blockType, parentBlockId, section.blocks)
        : createAddElementRootBlockInput(blockType, section.blocks);
      if (propsOverride) {
        blockInput.props = { ...blockInput.props, ...propsOverride };
      }

      const newBlockId = addBlock(section.id, blockInput);

      if (newBlockId) {
        const sectionUiKey = getSectionUiKey(section);
        // Defer until the block is mounted so selection toolbar anchors correctly.
        requestAnimationFrame(() => {
          setActiveBlock(sectionUiKey, newBlockId);
          getDocument()
            ?.querySelector(`[data-block-selection-root="${newBlockId}"]`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      }

      return newBlockId;
    },
    [addBlock, parentBlockId, section, setActiveBlock],
  );

  const handleAddBlock = useCallback(
    (blockType: AddElementBlockType) => {
      if (
        blockType === 'image' ||
        blockType === 'video' ||
        blockType === 'embed' ||
        blockType === 'button'
      ) {
        onOpenChange(false);
        setDeferredModal(blockType);
        return;
      }

      const propsOverride = blockType === 'form' ? withDefaultForm({}, forms) : undefined;

      insertAndScroll(blockType, propsOverride);
      onOpenChange(false);
    },
    [forms, insertAndScroll, onOpenChange],
  );

  const closeModal = useCallback(() => setDeferredModal(null), []);

  const handleAssetSelected = useCallback(
    (asset: SelectedAssetData) => {
      if (deferredModal !== 'image' && deferredModal !== 'video') return;
      const mediaValue = {
        url: asset.url,
        assetId: asset.assetId,
        folderId: asset.folderId,
        ...(deferredModal === 'image'
          ? { alt: asset.altText, variants: asset.variants, contentType: asset.contentType }
          : { description: asset.description }),
      };
      insertAndScroll(deferredModal, { [deferredModal]: mediaValue });
      setDeferredModal(null);
    },
    [deferredModal, insertAndScroll],
  );

  const handleYouTubeSelected = useCallback(
    (youtubeUrl: string) => {
      insertAndScroll('video', { video: { url: youtubeUrl, provider: 'youtube' } });
      setDeferredModal(null);
    },
    [insertAndScroll],
  );

  const handleEmbedSave = useCallback(
    (url: string, html: string) => {
      insertAndScroll('embed', { url: normalizeEmbedUrl(url), html });
      setDeferredModal(null);
    },
    [insertAndScroll],
  );

  const handleButtonSave = useCallback(
    (value: ButtonConfig) => {
      insertAndScroll('button', { ...value });
      setDeferredModal(null);
    },
    [insertAndScroll],
  );

  return (
    <>
      <Popover open={isOpen} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            colorScheme="secondary"
            className={cn('h-10 font-semibold', 'shadow-lg', isOpen && 'invisible')}
          >
            <Icon icon={faPlus} />
            Add Element
          </Button>
        </PopoverTrigger>
        <PopoverContent
          side="bottom"
          align="start"
          sideOffset={-40}
          collisionPadding={24}
          className="w-70 rounded-md border-border bg-card p-0 shadow-xl"
        >
          <Heading5 className="border-b px-4 py-3">Add Element</Heading5>
          <Button
            variant="ghost"
            colorScheme="secondary"
            size="icon-sm"
            className="size-7 absolute top-3 right-4"
            aria-label="Close add element"
            onClick={() => onOpenChange(false)}
          >
            <Icon icon={faXmark} />
          </Button>

          <div className="space-y-2 p-4">
            {availableBlocks.map((option) => (
              <Button
                key={option.blockType}
                variant="outline"
                colorScheme="secondary"
                className="h-12 w-full justify-start font-medium"
                onClick={() => handleAddBlock(option.blockType)}
              >
                <Icon icon={option.icon} className="size-4 text-foreground" />
                {option.label}
              </Button>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      {(deferredModal === 'image' || deferredModal === 'video') && (
        <ImageSelectorModal
          isOpen
          onClose={closeModal}
          onSelectImage={handleAssetSelected}
          onYouTubeSelect={deferredModal === 'video' ? handleYouTubeSelected : undefined}
          fileTypes={deferredModal === 'image' ? 'images' : 'videos'}
          selectLabel="Apply"
          enableCreateImage={deferredModal === 'image'}
        />
      )}

      <EmbedSettingsDialog
        open={deferredModal === 'embed'}
        onOpenChange={(next) => {
          if (!next) setDeferredModal(null);
        }}
        onSave={handleEmbedSave}
      />

      <ButtonSettingsDialog
        open={deferredModal === 'button'}
        onOpenChange={(next) => {
          if (!next) setDeferredModal(null);
        }}
        initialValue={NEW_BUTTON_INITIAL_VALUE}
        onSave={handleButtonSave}
      />
    </>
  );
}
